import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  RadioGroup,
  Radio,
  FormControlLabel,
} from "@mui/material";
import { Mic } from "@mui/icons-material";
import { voiceService, VoiceSettings } from "../services/voiceService";
import SavingOverlay from "../components/SavingOverlay";
import { colors, typography } from "../theme/designSystem";

interface Props {
  onToast: (message: string, type: "success" | "error") => void;
}

/** Motor de transcrição de todo ditado do sistema (assistentes e campos com microfone). */
const VoiceTranscriptionConfig: React.FC<Props> = ({ onToast }) => {
  const [saving, setSaving] = useState(false);
  const [voice, setVoice] = useState<VoiceSettings | null>(null);
  const [engine, setEngine] = useState<"webspeech" | "whisper">("webspeech");

  useEffect(() => {
    let alive = true;
    voiceService
      .getSettings()
      .then((v) => {
        if (!alive) return;
        setVoice(v);
        setEngine(v.configured);
      })
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, []);

  const handleEngine = async (value: "webspeech" | "whisper") => {
    const anterior = engine;
    setEngine(value);
    setSaving(true);
    try {
      await voiceService.saveEngine(value);
      setVoice((v) => (v ? { ...v, configured: value } : v));
      onToast(
        value === "whisper"
          ? "Transcrição passa a rodar no servidor da clínica."
          : "Transcrição volta para o reconhecimento do navegador.",
        "success"
      );
    } catch {
      setEngine(anterior);
      onToast("Não foi possível trocar o motor de transcrição.", "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ mt: 4, pt: 3, borderTop: "1px solid #e9ecef" }}>
      <SavingOverlay open={saving} />

      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
        <Mic sx={{ color: colors.primary }} />
        <Typography sx={{ fontWeight: 600, color: colors.textPrimary }}>
          Transcrição de voz
        </Typography>
      </Box>

      <Typography variant="body2" sx={{ color: colors.textSecondary, mb: 2, maxWidth: "56rem" }}>
        Vale para todo ditado do sistema, inclusive o modo conversa dos assistentes. As duas opções
        não são equivalentes — a diferença é para onde vai o áudio do atendimento.
      </Typography>

      <RadioGroup
        value={engine}
        onChange={(e) => handleEngine(e.target.value as "webspeech" | "whisper")}
      >
        <FormControlLabel
          value="webspeech"
          control={<Radio sx={{ "&.Mui-checked": { color: colors.primary } }} />}
          label={
            <Box sx={{ py: 0.5 }}>
              <Typography sx={{ fontSize: typography.fontSize.sm, fontWeight: 600 }}>
                Reconhecimento do navegador
              </Typography>
              <Typography sx={{ fontSize: typography.fontSize.sm, color: colors.textSecondary }}>
                Não consome o servidor, mas no Chrome <strong>o áudio é enviado ao Google</strong>,
                e no Firefox não funciona — quem usa Firefox fica sem ditado.
              </Typography>
            </Box>
          }
        />
        <FormControlLabel
          value="whisper"
          disabled={!voice?.serverReady}
          control={<Radio sx={{ "&.Mui-checked": { color: colors.primary } }} />}
          label={
            <Box sx={{ py: 0.5 }}>
              <Typography sx={{ fontSize: typography.fontSize.sm, fontWeight: 600 }}>
                Transcrição no servidor da clínica
              </Typography>
              <Typography sx={{ fontSize: typography.fontSize.sm, color: colors.textSecondary }}>
                O áudio <strong>não sai da clínica</strong> e funciona em qualquer navegador. Em
                troca usa processamento do servidor, então transcreve uma de cada vez: em horário
                de pico a segunda espera alguns segundos.
              </Typography>
            </Box>
          }
        />
      </RadioGroup>

      {!voice?.serverReady && (
        <Typography sx={{ fontSize: typography.fontSize.sm, color: colors.textSecondary, mt: 1 }}>
          A transcrição no servidor ainda não está disponível neste ambiente. Enquanto não
          estiver, o ditado usa o navegador mesmo que esteja escolhida aqui.
        </Typography>
      )}
    </Box>
  );
};

export default VoiceTranscriptionConfig;
